import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Play, Database, AlertTriangle, GitCompare, Link2, Eye, X, CloudUpload, CheckCircle2, XCircle, PowerOff, Clock } from "lucide-react";

const SOURCES = [
  { key: "entry_closings", label: "Entry Closings", url: "https://pbj.prorodeo.org/entriesclosings" },
  { key: "long_listings", label: "Long Listings", url: "https://pbj.prorodeo.org/longlistings" },
  { key: "ground_rules", label: "Ground Rules", url: "https://pbj.prorodeo.org/groundrules" },
];
const SAMPLES = {
  entry_closings: `rodeo,state,entry close,time,events\nPonoka Stampede,AB,Dec 20 2026,10:00 AM MT,All\nCheyenne Frontier Days,WY,Nov 15 2026,,Bull Riding`,
  long_listings: `rodeo,city,state,start,end,added money,arena,events,stock contractor\nCheyenne Frontier Days,Cheyenne,WY,Jul 18 2026,Jul 27 2026,"$1,000,000",Frontier Park Arena,All,Powder River`,
  ground_rules: `rodeo,state,rules\nCheyenne Frontier Days,WY,"Long score. Two-head average in timed events."`,
};
const fmtTs = (t) => (t ? new Date(t).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const ORIGIN = {
  upload: "Manual upload", preview_collector: "Preview collector", manual_collector: "Preview collector",
  prod_collector: "Production collector", cron: "Collector", playwright: "Collector",
};

const COLLECTOR_BADGE = {
  healthy: { label: "HEALTHY", cls: "text-[#2E7D32] bg-[#E8F5E9]" },
  error: { label: "COLLECTOR ERROR", cls: "text-[#C8000F] bg-[#FDECEC]" },
  offline: { label: "COLLECTOR OFFLINE", cls: "text-[#B06A00] bg-[#FDF3E3]" },
  disabled: { label: "DISABLED", cls: "text-slate-500 bg-slate-100" },
  idle: { label: "IDLE", cls: "text-slate-500 bg-slate-100" },
};

export default function PrcaDataManager() {
  const [dash, setDash] = useState({ worker: false, prod_target: null, sources: [] });
  const [tab, setTab] = useState("entry_closings");
  const [view, setView] = useState("import"); // import | changes | unmatched
  const [errModal, setErrModal] = useState(null); // {label, error}
  const loadDash = () => api.get("/admin/ingest/dashboard").then((r) => setDash(r.data || { sources: [] })).catch(() => {});
  useEffect(() => { loadDash(); }, []);
  const worker = dash.worker;

  const openUpload = (src) => { setTab(src); setView("import"); document.getElementById("pbj-panels")?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  return (
    <div data-testid="prca-data-manager" className="rounded-xl border-2 border-[#C8000F]/25 bg-[#FFF8F8] p-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <h2 className="font-display text-lg font-bold flex items-center gap-2"><Database className="w-5 h-5 text-[#C8000F]" /> Data Sources → PRCA PBJ</h2>
        {worker ? <SyncToProd target={dash.prod_target} onDone={loadDash} /> : null}
      </div>
      <p className="text-sm text-slate-500 mb-3">
        pbj.prorodeo.org has no public feed (Blazor Server). The headless-browser collector runs on the <b>preview worker</b> and pushes results to production; production itself never runs a browser. A failed scrape never wipes last-known-good data.
      </p>
      <div className={`text-xs rounded-lg px-3 py-2 mb-4 inline-flex items-center gap-2 ${worker ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-slate-100 text-slate-600"}`} data-testid="pbj-env-banner">
        {worker ? <><CloudUpload className="w-4 h-4" /> This environment is the <b>collector worker</b> → syncing to {dash.prod_target || "production"}.</>
          : <><PowerOff className="w-4 h-4" /> Automated collector is <b>unavailable on this server</b>. Data is synced in from the preview collector; use <b>Upload</b> as a manual fallback.</>}
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#E4E7EB] bg-white mb-5">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 border-b border-[#EEF0F2]">
            <th className="px-3 py-2">Source</th><th className="px-3 py-2">Collector</th><th className="px-3 py-2">Data</th>
            <th className="px-3 py-2">Last Successful</th><th className="px-3 py-2">Last Attempt</th><th className="px-3 py-2 text-right">Actions</th></tr></thead>
          <tbody>
            {SOURCES.map((s) => {
              const d = dash.sources.find((x) => x.source_type === s.key) || {};
              const cb = COLLECTOR_BADGE[d.collector_status] || COLLECTOR_BADGE.idle;
              const current = d.data_status === "current";
              const origin = d.data_origin ? (ORIGIN[d.data_origin] || "Collector") : null;
              return (
                <tr key={s.key} data-testid={`pbj-src-${s.key}`} className="border-b border-[#F3F4F6] last:border-0 align-top">
                  <td className="px-3 py-3 font-semibold">{s.label}</td>
                  <td className="px-3 py-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${cb.cls}`}>{cb.label}</span>
                    {d.collector_status === "disabled" && <span className="block text-[10px] text-slate-400 mt-1 max-w-[180px]">Automated collector unavailable on this server</span>}
                    {d.last_error && (d.collector_status === "error" || d.collector_status === "offline") && (
                      <button onClick={() => setErrModal({ label: s.label, error: d.last_error })} data-testid={`pbj-view-error-${s.key}`}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#C8000F] hover:underline"><Eye className="w-3 h-3" /> View error</button>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${current ? "text-[#2E7D32] bg-[#E8F5E9]" : "text-slate-500 bg-slate-100"}`}>{current ? "CURRENT" : "NO DATA"}</span>
                    <span className="block text-[11px] text-slate-600 mt-1">{current ? `${d.records} records` : "No data yet"}</span>
                    {s.key === "long_listings" && d.ground_rules_count != null && (
                      <span className="block text-[10px] text-[#2E7D32] font-semibold mt-0.5">{d.ground_rules_count} with full ground rules</span>
                    )}
                    {s.key === "ground_rules" && d.note && (
                      <span className="block text-[10px] text-[#B06A00] mt-0.5 max-w-[200px] leading-snug">{d.note}</span>
                    )}
                    {origin && <span className="block text-[10px] text-slate-400">via {origin}</span>}
                  </td>
                  <td className="px-3 py-3 text-slate-500 text-[12px] whitespace-nowrap">{fmtTs(d.last_successful_at)}</td>
                  <td className="px-3 py-3 text-slate-500 text-[12px] whitespace-nowrap">
                    {fmtTs(d.last_attempt_at)}
                    {d.last_attempt_status && d.last_attempt_status !== "healthy" && <span className="block text-[10px] text-[#C8000F]">{d.last_attempt_status}</span>}
                    {d.last_push && d.last_push.pushed === false && <span className="block text-[10px] text-[#B06A00]" title={d.last_push.detail || d.last_push.reason || ""}>prod push failed</span>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5 justify-end">
                      <RunNow source={s.key} available={d.collector_available} onDone={loadDash} />
                      <button onClick={() => openUpload(s.key)} data-testid={`pbj-upload-${s.key}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#003087] bg-white border border-[#003087]/40 rounded-lg px-3 py-1.5 hover:bg-[#F0F4FB]"><Upload className="w-3.5 h-3.5" /> Upload</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div id="pbj-panels" className="flex flex-wrap gap-2 mb-4">
        {SOURCES.map((s) => (
          <button key={s.key} onClick={() => setTab(s.key)} data-testid={`pbj-tab-${s.key}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${tab === s.key ? "bg-[#C8000F] text-white border-[#C8000F]" : "bg-white text-slate-600 border-[#E4E7EB]"}`}>{s.label}</button>
        ))}
        <span className="mx-1 w-px bg-[#E4E7EB]" />
        {[["import", "Upload", Upload], ["changes", "Changes", GitCompare], ["unmatched", "Unmatched", Link2]].map(([v, lbl, Icon]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`pbj-view-${v}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${view === v ? "bg-[#003087] text-white border-[#003087]" : "bg-white text-slate-600 border-[#E4E7EB]"}`}><Icon className="w-3.5 h-3.5" /> {lbl}</button>
        ))}
      </div>

      {view === "import" && <ImportPanel source={tab} onDone={loadDash} />}
      {view === "changes" && <ChangesPanel />}
      {view === "unmatched" && <UnmatchedPanel onDone={loadDash} />}

      {errModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setErrModal(null)} data-testid="pbj-error-modal">
          <div className="bg-white rounded-xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-[#C8000F]" /> {errModal.label} — collector error</h3>
              <button onClick={() => setErrModal(null)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <pre className="text-xs bg-[#FBFBFC] border border-[#E4E7EB] rounded-lg p-3 whitespace-pre-wrap break-words max-h-72 overflow-auto text-slate-700">{errModal.error}</pre>
            <p className="text-xs text-slate-400 mt-3">Last-known-good data is retained — a failed scrape never deletes existing records.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function SyncToProd({ target, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.post("/admin/prca/sync-to-prod");
      toast.success("Sync started — collecting PBJ data and pushing to production. Refresh in ~1 min.");
      setTimeout(() => onDone?.(), 60000);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Sync failed to start"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid="pbj-sync-to-prod"
      className="inline-flex items-center gap-2 text-sm font-bold text-white bg-[#003087] rounded-lg px-4 py-2 disabled:opacity-60">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />} Sync to Production
    </button>
  );
}

function RunNow({ source, available, onDone }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/prca/run-now?source_type=${source}`);
      if (data.status === "healthy") {
        const p = data.push;
        toast.success(`Collected ${data.records_in}${p?.pushed ? " · pushed to production" : p ? " · prod push failed (see dashboard)" : ""}`);
      } else if (/Zero records/i.test(data.error || "")) toast.message("Collector ran but the site returned no rows — existing data kept.");
      else toast.error(`Collector failed — data kept. ${data.error || ""}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Run failed"); } finally { setBusy(false); onDone?.(); }
  };
  if (!available) {
    return (
      <button disabled data-testid={`pbj-run-${source}`} title="Automated collector unavailable on this server"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 bg-slate-100 rounded-lg px-3 py-1.5 cursor-not-allowed">
        <Play className="w-3.5 h-3.5" /> Run Now
      </button>
    );
  }
  return (
    <button onClick={run} disabled={busy} data-testid={`pbj-run-${source}`}
      className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[#C8000F] rounded-lg px-3 py-1.5 disabled:opacity-60">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run Now
    </button>
  );
}

function ImportPanel({ source, onDone }) {
  const [text, setText] = useState("");
  const [fmt, setFmt] = useState("csv");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) { toast.error("Paste rows first"); return; }
    setBusy(true);
    try {
      const body = { source_type: source, format: fmt, source_url: SOURCES.find((s) => s.key === source)?.url };
      if (fmt === "json") { try { body.records = JSON.parse(text); } catch { toast.error("Invalid JSON"); setBusy(false); return; } }
      else body.text = text;
      const { data } = await api.post("/admin/prca/ingest", body);
      if (data.status === "healthy") { toast.success(`Ingested ${data.records_in} · ${data.records_new} new · ${data.records_changed} changed`); setText(""); }
      else toast.error(data.error || "Ingest failed");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Ingest failed"); } finally { setBusy(false); onDone?.(); }
  };
  return (
    <div data-testid="pbj-import-panel">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-sm text-slate-500">Format:</span>
        {["csv", "json"].map((f) => (
          <label key={f} className="text-sm text-slate-600 flex items-center gap-1.5"><input type="radio" checked={fmt === f} onChange={() => setFmt(f)} data-testid={`pbj-fmt-${f}`} /> {f.toUpperCase()}</label>
        ))}
        <button onClick={() => { setFmt("csv"); setText(SAMPLES[source]); }} className="text-xs text-slate-500 underline ml-auto">Load sample</button>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={7} data-testid="pbj-import-input"
        placeholder={fmt === "csv" ? SAMPLES[source] : '[{"rodeo":"...","state":"..","entry close":"Dec 20 2026"}]'}
        className="w-full font-mono text-[12px] rounded-lg border border-[#D7DBE0] px-3 py-2 focus:outline-none focus:border-slate-500 resize-y" />
      <button onClick={save} disabled={busy} data-testid="pbj-import-save"
        className="mt-3 inline-flex items-center gap-2 text-white bg-[#C8000F] rounded-lg px-5 py-2.5 text-sm font-bold disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Ingest {SOURCES.find((s) => s.key === source)?.label}
      </button>
      <p className="text-xs text-slate-400 mt-2">Full raw rows are stored alongside parsed fields; changes are versioned and logged.</p>
    </div>
  );
}

function ChangesPanel() {
  const [items, setItems] = useState(null);
  const [imp, setImp] = useState(true);
  useEffect(() => { api.get(`/admin/ingest/changes?important=${imp}`).then((r) => setItems(r.data || [])).catch(() => setItems([])); }, [imp]);
  return (
    <div data-testid="pbj-changes-panel">
      <label className="text-sm text-slate-600 flex items-center gap-1.5 mb-3"><input type="checkbox" checked={imp} onChange={(e) => setImp(e.target.checked)} /> Important changes only</label>
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /> : items.length === 0 ? <p className="text-sm text-slate-400">No changes recorded yet.</p> : (
        <div className="rounded-lg border border-[#E4E7EB] bg-white max-h-80 overflow-y-auto divide-y divide-[#F0F1F3]">
          {items.map((c) => (
            <div key={c.id} className="px-3 py-2 text-sm flex items-start gap-2">
              {c.important && <AlertTriangle className="w-4 h-4 text-[#C8000F] shrink-0 mt-0.5" />}
              <div><b>{c.rodeo_name}</b> <span className="text-slate-400">· {c.source_type}</span>
                {c.kind === "created" ? <span className="text-[#2E7D32]"> · new record</span> :
                  <span className="text-slate-600"> · {c.field}: <span className="line-through text-slate-400">{c.old || "—"}</span> → <b>{c.new || "—"}</b></span>}
                <span className="block text-[10px] text-slate-400">{fmtTs(c.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function UnmatchedPanel({ onDone }) {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/ingest/unmatched").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="pbj-unmatched-panel">
      {items === null ? <Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /> : items.length === 0 ? <p className="text-sm text-slate-400">Everything is matched to a rodeo. 🎉</p> : (
        <div className="space-y-2">
          {items.map((u) => <UnmatchedRow key={u.id} u={u} onLinked={() => { load(); onDone?.(); }} />)}
        </div>
      )}
    </div>
  );
}

function UnmatchedRow({ u, onLinked }) {
  const [q, setQ] = useState(`${u.name} ${u.state || ""}`.trim());
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const search = async () => {
    setSearching(true);
    try { const { data } = await api.get(`/admin/ingest/rodeo-search?q=${encodeURIComponent(q)}`); setResults(data || []); }
    catch { setResults([]); } finally { setSearching(false); }
  };
  const link = async (rid) => {
    try { await api.post("/admin/ingest/match", { unmatched_id: u.id, rodeo_id: rid }); toast.success("Linked"); onLinked?.(); }
    catch { toast.error("Link failed"); }
  };
  return (
    <div className="rounded-lg border border-[#E4E7EB] bg-white p-3" data-testid={`pbj-unmatched-${u.id}`}>
      <div className="text-sm font-semibold">{u.name} <span className="text-slate-400 font-normal">· {[u.city, u.state].filter(Boolean).join(", ")} · {u.source_type}</span></div>
      <div className="flex gap-2 mt-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 text-sm rounded border border-[#D7DBE0] px-2 py-1.5" placeholder="Search a rodeo to link…" data-testid={`pbj-match-input-${u.id}`} />
        <button onClick={search} disabled={searching} className="text-xs font-semibold px-3 py-1.5 rounded bg-[#003087] text-white">{searching ? "…" : "Search"}</button>
      </div>
      {results.length > 0 && (
        <div className="mt-2 rounded border border-[#EEF0F2] divide-y divide-[#F3F4F6] max-h-44 overflow-y-auto">
          {results.map((r) => (
            <button key={r.id} onClick={() => link(r.id)} data-testid={`pbj-link-${u.id}-${r.id}`} className="w-full text-left px-2 py-1.5 text-sm hover:bg-[#F7F8FA] flex items-center justify-between">
              <span>{r.name} <span className="text-slate-400">· {[r.city, r.state].filter(Boolean).join(", ")}</span></span>
              <Link2 className="w-3.5 h-3.5 text-[#003087]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
